/**
 * EnneagramBadge — UX-021 contrast redesign.
 *
 * Spec (docs/architecture/responsive-plan.md §5):
 * - Large numeral (text-3xl bold) over a neutral surface (`bg-card`).
 * - Label below at `text-sm` minimum (never < 14px).
 * - Foreground text uses design tokens (`text-foreground` /
 *   `text-muted-foreground`) which hit ≥ 4.5:1 against `--card` in both
 *   light and dark themes.
 * - Optional accent dot may carry the symbolic type color but never the
 *   contrast for the text.
 *
 * Two sizes:
 * - `compact`  → history rows, list items (min-w 64px, py-3 auto-height, numeral text-3xl).
 * - `featured` → hero cards (min-w 120px, h-24, numeral text-5xl).
 */
import { type CSSProperties } from "react";
import { cn } from "@/lib/utils";

export type EnneagramType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export interface EnneagramBadgeProps {
  type: EnneagramType | number;
  label: string;
  size?: "compact" | "featured";
  accentColor?: string;
  className?: string;
}

export function EnneagramBadge({
  type,
  label,
  size = "compact",
  accentColor,
  className,
}: EnneagramBadgeProps) {
  const isFeatured = size === "featured";
  const ariaLabel = `Código C${type} do Eneagrama: ${label}`;

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={cn(
        "relative inline-flex flex-col items-center justify-center rounded-xl border border-border bg-card px-3 py-2 text-card-foreground",
        isFeatured ? "min-w-[120px] h-24" : "min-w-[110px] py-3",
        className,
      )}
    >
      {accentColor ? (
        <span
          aria-hidden="true"
          className="absolute left-0 top-2 bottom-2 w-1 rounded-r"
          style={{ backgroundColor: accentColor } satisfies CSSProperties}
        />
      ) : null}
      <span
        className={cn(
          "font-bold leading-none text-foreground",
          isFeatured ? "text-5xl" : "text-3xl",
        )}
      >
        C{type}
      </span>
      <span
        title={label}
        className={cn(
          "mt-1 font-medium text-muted-foreground truncate max-w-[150px]",
          isFeatured ? "text-base max-w-[180px]" : "text-sm w-full text-center",
        )}
      >
        {label}
      </span>
    </div>
  );
}
